import { useEffect, useState, useRef } from 'react';
import { Tag, Input, Tooltip, message } from 'antd';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import { PlusOutlined } from '@ant-design/icons';
import { produce } from 'immer';
import { useTranslate } from '@tolgee/react';

type QuestionExampleProps = {
  value?: string[];
  onChange?: (value: string[]) => void;
};

type InputRef = any;

function QuestionExample(props: QuestionExampleProps) {
  const { value = [], onChange } = props;
  const [examples, setExamples] = useState<string[]>(value);
  const [inputVisible, setInputVisible] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [editInputIndex, setEditInputIndex] = useState(-1);
  const [editInputValue, setEditInputValue] = useState('');
  const inputRef = useRef<InputRef>(null);
  const editInputRef = useRef<InputRef>(null);
  const { t } = useTranslate();

  useEffect(() => {
    if (inputVisible) {
      inputRef.current?.focus();
    }
  }, [inputVisible]);

  useEffect(() => {
    editInputRef.current?.focus();
  }, [inputValue]);

  const handleClose = (removedTag: string) => {
    setExamples(
      produce((draft) => {
        draft.splice(examples.indexOf(removedTag), 1);
      }),
    );
  };

  const showInput = () => {
    setInputVisible(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputConfirm = () => {
    inputValue &&
      setExamples(
        produce((draft) => {
          draft.push(inputValue);
        }),
      );
    setInputVisible(false);
    setInputValue('');
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditInputValue(e.target.value);
  };

  const handleEditInputConfirm = () => {
    if (editInputValue) {
      if (editInputValue.length < 500) {
        setExamples(
          produce((draft) => {
            draft.splice(editInputIndex, 1, editInputValue);
          }),
        );

        setEditInputIndex(-1);
        setInputValue('');
      } else {
        message.error(t('knowledge.questionExampleLengthLimit'));
      }
    }
  };

  useEffect(() => {
    onChange && onChange(examples);
  }, [examples, onChange]);
  return (
    <Container>
      {examples.map((example, index) => {
        if (editInputIndex === index) {
          return (
            <Input
              ref={editInputRef}
              key={example}
              size="small"
              className={css`
                width: 240px;
                margin-right: 12px;
              `}
              value={editInputValue}
              onChange={handleEditInputChange}
              onBlur={handleEditInputConfirm}
              onPressEnter={handleEditInputConfirm}
            />
          );
        }

        const isLongTag = example.length > 20;

        const exampleElem = (
          <Tag
            className={css`
              background: #f0f2f5;
              color: #515359;
            `}
            closable
            key={example}
            onClose={() => handleClose(example)}
          >
            <span
              onDoubleClick={(e) => {
                setEditInputIndex(index);
                setEditInputValue(example);
                e.preventDefault();
              }}
            >
              {isLongTag ? `${example.slice(0, 20)}...` : example}
            </span>
          </Tag>
        );
        return isLongTag ? (
          <Tooltip title={example} key={example}>
            {exampleElem}
          </Tooltip>
        ) : (
          exampleElem
        );
      })}
      {inputVisible && (
        <Input
          ref={inputRef}
          type="text"
          size="small"
          className={css`
            width: 240px;
          `}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputConfirm}
          onPressEnter={handleInputConfirm}
        />
      )}
      {!inputVisible && (
        <Tag
          className={css`
            border: 1px rgb(51, 112, 255) dashed;
            color: #336fff;
            padding: 0px 16px;
          `}
          onClick={showInput}
        >
          <PlusOutlined /> {t('knowledge.新增x', { x: '' })}
        </Tag>
      )}
    </Container>
  );
}

const Container = styled.div`
  .ant-tag {
    height: 24px;
    line-height: 22px;
  }
`;

export default QuestionExample;
